import { Box, Checkbox, Text } from "@chakra-ui/react";
import type { ReactNode } from "react";

/**
 * Full-bleed banding for the requirement forms: a grey band names each group of fields, and option
 * rows sit flush against the same edges. The negative margin cancels the 6-unit padding that both
 * DrawerBody and ModalBody apply, so the form reads as one column of rows rather than a boxed card.
 */
export const FormSectionBand = ({ children }: { children: ReactNode }) => (
  <Box
    mx="-6"
    px="6"
    py="2"
    bg="smBackgroundSecondary"
    borderTopWidth="1px"
    borderBottomWidth="1px"
    borderColor="smBorder"
    fontSize="xs"
    fontWeight="semibold"
    letterSpacing="0.06em"
    textTransform="uppercase"
    color="smMuted"
  >
    {children}
  </Box>
);

interface OptionRowProps {
  isChecked: boolean;
  onChange: (checked: boolean) => void;
  title: ReactNode;
  hint: ReactNode;
  /**
   * The rule under the row separates it from whatever comes next. Turn it off where nothing does —
   * a line against the bottom of a dialog divides the row from the air below it.
   */
  showDivider?: boolean;
}

/**
 * A yes/no choice as a row: the label carries the weight, the hint says what it means in practice.
 *
 * The text sits inside the Checkbox rather than beside it, so the row is one label rather than a label
 * wrapping Chakra's own — nested labels forward the same click twice, which reads as a dead checkbox.
 */
export const FormOptionRow = ({ isChecked, onChange, title, hint, showDivider = true }: OptionRowProps) => (
  <Checkbox
    isChecked={isChecked}
    onChange={(e) => onChange(e.target.checked)}
    colorScheme="teal"
    alignItems="flex-start"
    spacing="3"
    w="full"
    mx="-6"
    px="6"
    py="3"
    borderBottomWidth={showDivider ? "1px" : "0"}
    borderColor="smBorder"
    _hover={{ bg: "smBackgroundSecondary" }}
    sx={{ ".chakra-checkbox__control": { marginTop: "0.15rem" } }}
  >
    <Box minW="0">
      <Text fontSize="sm" fontWeight="bold" color="smText">
        {title}
      </Text>
      <Text fontSize="xs" color="smMuted">
        {hint}
      </Text>
    </Box>
  </Checkbox>
);
